import Container from "@vfuk/core-container";
import { SelectInput, Stack, useDebounce } from "@ucchub/ui";
import Spacing from "@vfuk/core-spacing";
import { Controller, useForm } from "react-hook-form";
import Heading from "@vfuk/core-heading";
import SimpleGrid from "@vfuk/core-simple-grid";
import SmallButton from "@vfuk/core-small-button";
import Divider from "@vfuk/core-divider";
import { InteractiveIcon } from "@vfuk/core-interactive-icon";
import styled from "styled-components";
import { FilterData, FilterValues } from "../UserManagement.types";
import { t } from "i18next";
import { useEffect, useState } from "react";
import _ from "lodash";
import useCustomerList from "@ucchub/ui/src/cloudNumbers/hooks/customer/useCustomerList";
import { CustomerListItem } from "@components/customer/Customer.types";

export const FilterContainer = styled.div<{ $isHidden: boolean }>`
  display: ${(props) => (props.$isHidden ? "none" : "block")};
`;

interface FilterProps {
  onFilterClosed: () => void;
  onFilterApplied: (filterData?: FilterValues) => void;
  showStatusFilter: boolean;
  isOpen?: boolean;
  isVfAdmin: boolean;
}

const STATUS = [
  {
    label: "Active",
    value: "active",
  },
  {
    label: "Inactive",
    value: "inactive",
  },
];

const defaultValues: FilterData = {
  organisation: undefined,
  status: undefined,
};

const UsersFilterRow = ({
  onFilterClosed,
  onFilterApplied,
  isOpen,
  isVfAdmin,
}: FilterProps) => {
  const { handleSubmit, control, formState, reset } = useForm({
    mode: "onTouched",
  });
  const { isDirty } = formState;
  const [filterData, setFilterData] = useState<FilterData>();
  const [organisationSearchValue, setOrganisationSearchValue] = useState("");

  const { customerList, fetchCustomerList, clearCustomerList } =
    useCustomerList();
  const debouncedSearchValue = useDebounce<string>(
    organisationSearchValue,
    200
  );

  useEffect(() => {
    if (isOpen && isVfAdmin) {
      clearCustomerList();
      fetchCustomerList({ search: debouncedSearchValue, page: 0 });
    }
  }, [isOpen, debouncedSearchValue, isVfAdmin]);

  const onSubmit = handleSubmit((data) => {
    setFilterData(data);
    onFilterApplied({
      status: data.status?.value,
      orgId: data.organisation?.id,
    });
    onFilterClosed();
  });

  const onClearFilter = () => {
    setFilterData(defaultValues);
    reset(defaultValues);
    onFilterApplied();
  };

  useEffect(() => {
    if (isOpen) reset(filterData);
  }, [isOpen]);

  return (
    <FilterContainer $isHidden={!isOpen}>
      <Spacing spacingLevel={{ bottom: 6, top: 6 }}>
        <Container elevation paddingLevel={3}>
          <form onSubmit={onSubmit}>
            <SimpleGrid
              layout={{
                sm: [12],
                md: [12, 6, 6, 6, 6, 6, 6, 6, 6],
                xl: [12, 4, 4, 4, 4, 4, 4, 4, 4],
              }}
              spacing={3}
            >
              <Stack justifyContent="space-between" spacing={1}>
                <Spacing spacingLevel={{ left: 1, top: 2, bottom: 3 }}>
                  <Heading level={5} weight={3}>
                    Filters
                  </Heading>
                </Spacing>
                <Spacing spacingLevel={{ right: 1 }}>
                  <InteractiveIcon
                    name="close"
                    srText="close"
                    onClick={onFilterClosed}
                    size={3}
                  />
                </Spacing>
              </Stack>
              {isVfAdmin && (
                <Controller
                  name="organisation"
                  control={control}
                  defaultValue={undefined}
                  render={({ field }) => (
                    <SelectInput
                      id="select_organization"
                      label="Organisation"
                      spaceForError={false}
                      options={customerList || []}
                      getOptionLabel={(option: CustomerListItem) => {
                        return option.name;
                      }}
                      getOptionValue={(option: CustomerListItem) => {
                        return option.id;
                      }}
                      onInputChange={(org: string) => {
                        setOrganisationSearchValue(org);
                      }}
                      {...field}
                    />
                  )}
                />
              )}
              <Controller
                name="status"
                control={control}
                defaultValue={undefined}
                render={({ field }) => (
                  <SelectInput
                    label={t("shared:users:filters.status_text")}
                    id="status"
                    {...field}
                    options={STATUS}
                  />
                )}
              />
            </SimpleGrid>

            <Divider appearance="secondary" />
            <Spacing spacingLevel={{ top: 3, right: 4 }}>
              <Stack justifyContent="right">
                <SimpleGrid spacing={4} verticalAlign="bottom">
                  <SmallButton
                    state={
                      !isDirty &&
                      _.values(_.omitBy(filterData, (v) => _.isUndefined(v)))
                        .length === 0
                        ? "disabled"
                        : undefined
                    }
                    text={t("shared:users:filters.clear_all")}
                    appearance="alt1"
                    onClick={onClearFilter}
                  />
                  <SmallButton
                    state={!isDirty ? "disabled" : undefined}
                    onClick={onSubmit}
                    text={t("shared:users:filters.apply")}
                    appearance="primary"
                  />
                </SimpleGrid>
              </Stack>
            </Spacing>
          </form>
        </Container>
      </Spacing>
    </FilterContainer>
  );
};

export default UsersFilterRow;
